/**
 * Persists grimoire frame blobs in IndexedDB so they survive page reloads.
 * All writes are serialised through a queue to keep the operation order.
 */
export default class FrameStore {

    static DB_NAME = "pocket-grimoire-frames";
    static STORE_NAME = "frames";

    constructor() {
        this.db = null;
        this.queue = Promise.resolve();
    }

    /**
     * Whether IndexedDB is usable in this environment.
     *
     * @return {Boolean}
     */
    static isSupported() {
        return typeof indexedDB !== "undefined";
    }

    /**
     * Opens the database, creating the object store on first run.
     *
     * @return {Promise.<IDBDatabase>}
     */
    open() {

        if (this.db) {
            return Promise.resolve(this.db);
        }

        this.db = new Promise((resolve, reject) => {

            const request = indexedDB.open(this.constructor.DB_NAME, 1);

            request.onupgradeneeded = () => {
                request.result.createObjectStore(this.constructor.STORE_NAME, {
                    autoIncrement: true
                });
            };
            request.onsuccess = () => resolve(request.result);
            request.onerror = () => reject(request.error);

        }).catch((error) => {

            this.db = null;
            throw error;

        });

        return this.db;

    }

    /**
     * Runs the given task after the previous one, keeping operations in order.
     *
     * @param  {Function} task
     *         Receives the database, returns a promise.
     * @return {Promise}
     */
    enqueue(task) {

        const run = this.queue.then(() => this.open().then(task));
        this.queue = run.catch(() => {});
        return run;

    }

    /**
     * Reads all stored frames in insertion order. Each record contains the
     * frame blob and a flag marking it for faster playback in the video.
     * Legacy records that only hold a blob are normalised.
     *
     * @return {Promise.<Array.<{blob: Blob, fast: Boolean}>>}
     */
    getAll() {

        return this.enqueue((db) => new Promise((resolve, reject) => {

            const request = db
                .transaction(this.constructor.STORE_NAME, "readonly")
                .objectStore(this.constructor.STORE_NAME)
                .getAll();

            request.onsuccess = () => resolve(request.result.map((record) => (
                record?.blob !== undefined
                ? record
                : { blob: record, fast: false }
            )));
            request.onerror = () => reject(request.error);

        }));

    }

    /**
     * Appends a frame to the store.
     *
     * @param  {Blob} blob
     *         The frame to store.
     * @param  {Boolean} fast
     *         Whether the frame should be played back faster in the video.
     * @return {Promise}
     */
    add(blob, fast = false) {

        return this.transact((store) => store.add({ blob, fast }));

    }

    /**
     * Removes all stored frames.
     *
     * @return {Promise}
     */
    clear() {

        return this.transact((store) => store.clear());

    }

    /**
     * Runs a write operation inside a read-write transaction, resolving when
     * the transaction completes.
     *
     * @param  {Function} operation
     *         Receives the object store.
     * @return {Promise}
     */
    transact(operation) {

        return this.enqueue((db) => new Promise((resolve, reject) => {

            const transaction = db.transaction(this.constructor.STORE_NAME, "readwrite");
            operation(transaction.objectStore(this.constructor.STORE_NAME));
            transaction.oncomplete = () => resolve();
            transaction.onerror = () => reject(transaction.error);

        }));

    }

}
